import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, BellRing, Copy, ExternalLink, FileSignature, Loader2, PhoneCall, RefreshCw, Send } from "lucide-react";
import { api } from "../../lib/api";
import {
  CATEGORY_BY_KEY, TODO_CATEGORIES, autoSendText, contractLine, extraDetail, headline, leftText, remindersLine, whenText,
} from "../../lib/contractTodo";
import { BTN_QUIET, errorDetail } from "./ContractCard";

const CHIP = "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors";
const ACTION = "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold disabled:opacity-40";

/**
 * "De rezolvat": every paid participant whose contract needs a person, one
 * row each, urgent first. The team is told here and nowhere else — no
 * emails — so each row carries what there is to know before the call.
 *
 * Its own tab in Contracte; `onBatch` opens the batch for participants
 * already paid, which is what the "De trimis" cases are waiting for.
 */
export default function ContractTodo({ data, loading, onReload, category, onCategory, onOpenEnrollment, onOpenContract, onBatch }) {
  const counts = data?.counts || {};
  const items = (data?.items || []).filter((i) => !category || i.category === category);
  const now = new Date();
  const sweep = data?.sweep;

  return (
    <section className="rounded-2xl border border-[#E5E7EB] bg-white" data-testid="contracts-todo">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#E5E7EB] px-4 py-3">
        <p className="max-w-xl text-sm text-[#6B7280]">
          Participanții plătiți al căror contract are nevoie de cineva. Echipa nu primește emailuri: totul e aici.
        </p>
        <div className="flex items-center gap-2 text-[11px] text-[#6B7280]">
          {sweep?.at && (
            <span title="Jobul care recitește contractele și trimite reamintirile, la 15 minute">
              Verificat automat {whenText(sweep.at, now)}
            </span>
          )}
          {data?.auto_send && (
            <span className={`rounded-full px-2 py-0.5 ${data.auto_send.enabled ? "bg-[#EAF4EE] text-[#2F6B4E]" : "bg-[#F3F4F6] text-[#6B7280]"}`}
              data-testid="todo-auto-send">
              {data.auto_send.enabled ? "trimitere automată pornită" : "trimitere automată oprită"}
            </span>
          )}
          {data && data.auto_remind === false && (
            <span className="rounded-full bg-[#FDF3E7] px-2 py-0.5 text-[#8a6a1a]">reamintirile automate sunt oprite</span>
          )}
          <button type="button" onClick={onReload} disabled={loading} className={BTN_QUIET} aria-label="Reîncarcă lista">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          </button>
          {onBatch && (
            <button type="button" onClick={onBatch} className={BTN_QUIET} data-testid="contracts-batch-open">
              <Send size={13} /> Trimite contractele participanților deja plătiți
            </button>
          )}
        </div>
      </div>

      {data?.auto_send?.pause?.until && new Date(data.auto_send.pause.until) > now && (
        <div className="mx-4 mt-3 flex items-start gap-2 rounded-lg bg-[#FDECEC] px-3 py-2 text-xs text-[#9b1f2c]" data-testid="todo-auto-send-paused">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>{autoSendText(data.auto_send, now)}. Verifică creditele din contul eSemneaza; trimiterea se reia singură.</span>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 px-4 pt-3" role="tablist" aria-label="Situații">
        <button type="button" role="tab" aria-selected={!category} onClick={() => onCategory("")}
          className={`${CHIP} ${!category ? "border-[#373A45] bg-[#373A45] text-white" : "border-[#E5E7EB] bg-white text-[#373A45] hover:bg-[#F5F6F6]"}`}
          data-testid="todo-chip-all">
          Toate <span className="tabular-nums opacity-80">{data?.items?.length ?? "—"}</span>
        </button>
        {TODO_CATEGORIES.map((c) => {
          const on = category === c.key;
          const n = counts[c.key] ?? 0;
          return (
            <button key={c.key} type="button" role="tab" aria-selected={on} onClick={() => onCategory(on ? "" : c.key)}
              className={`${CHIP} ${on ? "border-[#373A45] bg-[#373A45] text-white" : "border-[#E5E7EB] bg-white text-[#373A45] hover:bg-[#F5F6F6]"} ${!n && !on ? "opacity-50" : ""}`}
              data-testid={`todo-chip-${c.key}`}>
              <span className="h-2 w-2 rounded-full" style={{ background: c.dot }} aria-hidden="true" />
              {c.label} <span className="tabular-nums opacity-80">{n}</span>
            </button>
          );
        })}
      </div>

      {!data && (
        <div className="flex items-center gap-2 px-4 py-4 text-sm text-[#6B7280]">
          <Loader2 size={14} className="animate-spin" /> Se încarcă…
        </div>
      )}
      {data?.error && <p className="px-4 py-4 text-sm text-[#9b1f2c]">{data.error}</p>}
      {data && !data.error && items.length === 0 && (
        <p className="px-4 py-4 text-sm text-[#6B7280]" data-testid="todo-empty">
          {category ? "Nimic în această situație." : "Nimic de rezolvat. Toate contractele sunt semnate sau pe drum."}
        </p>
      )}

      <ul className="mt-2 divide-y divide-[#E5E7EB]">
        {items.map((it) => (
          <TodoRow key={it.enrollment_id} item={it} now={now} onReload={onReload}
            onOpenEnrollment={onOpenEnrollment} onOpenContract={onOpenContract} />
        ))}
      </ul>
    </section>
  );
}

function TodoRow({ item, now, onReload, onOpenEnrollment, onOpenContract }) {
  const [noting, setNoting] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null);
  const cat = CATEGORY_BY_KEY[item.category];
  const urgent = (item.was || item.category) === "urgent";
  const c = item.contract;
  const reminders = remindersLine(item, now);
  const extra = extraDetail(item);

  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText(item.participant.phone || "");
      toast.success("Telefon copiat.");
    } catch {
      toast.error("Nu am putut copia telefonul.");
    }
  };

  const remind = async () => {
    if (!window.confirm(`Trimiți acum o reamintire de semnare la ${item.participant.email}?`)) return;
    setBusy("remind");
    try {
      await api.post(`/admin/contracts/${c.id}/remind`);
      toast.success("Reamintirea a fost trimisă.");
      onReload();
    } catch (err) {
      toast.error(errorDetail(err, "Reamintirea nu a putut fi trimisă."));
    } finally {
      setBusy(null);
    }
  };

  const saveNote = async () => {
    setBusy("note");
    try {
      await api.post(`/admin/contracts/todo/${item.enrollment_id}/contact`, { note });
      toast.success("Contactarea a fost notată.");
      setNote(""); setNoting(false);
      onReload();
    } catch (err) {
      toast.error(errorDetail(err, "Nota nu a putut fi salvată."));
    } finally {
      setBusy(null);
    }
  };

  return (
    <li className={`px-4 py-3 ${item.category === "urgent" ? "bg-[#FDF6F6]" : ""}`} data-testid={`todo-row-${item.enrollment_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: cat?.dot }} aria-hidden="true" />
          <div className="min-w-0">
            <p className={`text-sm font-semibold ${item.category === "urgent" ? "text-[#9b1f2c]" : "text-[#373A45]"}`} data-testid="todo-headline">
              {headline(item, now)}
            </p>
            {extra.length > 0 && (
              <ul className="mt-1 space-y-0.5 text-xs text-[#6B7280]">
                {extra.map((d) => (
                  <li key={d} className="flex items-start gap-1"><AlertTriangle size={11} className="mt-0.5 shrink-0" /> {d}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <span className="whitespace-nowrap rounded-full bg-[#F3F4F6] px-2 py-0.5 text-[11px] font-semibold text-[#6B7280]">
          {cat?.label}{item.was ? ` · era ${CATEGORY_BY_KEY[item.was]?.label.toLowerCase()}` : ""}
        </span>
      </div>

      <dl className="mt-2 grid gap-x-6 gap-y-1 pl-[18px] text-[13px] sm:grid-cols-3">
        <div>
          <dt className="sr-only">Participant</dt>
          <dd className="font-medium text-[#373A45]">{item.participant.name || "—"}</dd>
          <dd className="flex items-center gap-1.5 text-[#373A45]">
            <a href={`tel:${(item.participant.phone || "").replace(/\s/g, "")}`} className="tabular-nums hover:underline">
              {item.participant.phone || "fără telefon"}
            </a>
            {item.participant.phone && (
              <button type="button" onClick={copyPhone} className="text-[#6B7280] hover:text-[#965EA7]" aria-label="Copiază telefonul"
                data-testid="todo-copy-phone">
                <Copy size={12} />
              </button>
            )}
          </dd>
          <dd className="truncate text-xs text-[#6B7280]">{item.participant.email}</dd>
        </div>
        <div>
          <dt className="sr-only">Grupă</dt>
          <dd className="text-[#373A45]">{item.group?.name || <span className="text-[#6B7280]">fără grupă</span>}</dd>
          {item.first_session_at && (
            <dd className="text-xs text-[#6B7280]">
              Prima sesiune {whenText(item.first_session_at, now)}
              {!item.started && item.hours_left != null && (
                <span className={urgent ? "font-semibold text-[#9b1f2c]" : ""}> · peste {leftText(item.hours_left)}</span>
              )}
              {item.started && <span className="font-semibold text-[#9b1f2c]"> · a început</span>}
            </dd>
          )}
        </div>
        <div>
          <dt className="sr-only">Contract</dt>
          <dd className="text-[#373A45]">{contractLine(item, now)}</dd>
          {reminders && <dd className="text-xs text-[#6B7280]">{reminders}</dd>}
          {item.next_step && item.category !== "scheduled" && (
            <dd className="text-xs text-[#6B7280]">
              Următorul pas: reamintirea {item.next_step.step}, {whenText(item.next_step.at, now)}
            </dd>
          )}
          {item.contacts > 0 && item.category !== "contacted" && (
            <dd className="text-xs text-[#6B7280]">Contactat de {item.contacts} {item.contacts === 1 ? "dată" : "ori"}</dd>
          )}
        </div>
      </dl>

      <div className="mt-2.5 flex flex-wrap gap-2 pl-[18px]">
        <button type="button" onClick={() => setNoting((v) => !v)}
          className={`${ACTION} border-[#965EA7] bg-[#965EA7] text-white hover:bg-[#7A4986]`} data-testid="todo-note-open">
          <PhoneCall size={12} /> Notează contactarea
        </button>
        {c?.status === "sent" && (
          <button type="button" onClick={remind} disabled={busy === "remind"}
            className={`${ACTION} border-[#965EA7] text-[#965EA7] hover:bg-[#F0E7F4]`} data-testid="todo-remind">
            {busy === "remind" ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />} Retrimite reamintirea
          </button>
        )}
        {onOpenEnrollment && (
          <button type="button" onClick={() => onOpenEnrollment(item.enrollment_id)}
            className={`${ACTION} border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]`} data-testid="todo-open-enrollment">
            <ExternalLink size={12} /> Deschide înscrierea
          </button>
        )}
        {c?.id && (
          <button type="button" onClick={() => onOpenContract(c.id)}
            className={`${ACTION} border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]`} data-testid="todo-open-contract">
            <FileSignature size={12} /> Deschide contractul
          </button>
        )}
      </div>

      {noting && (
        <div className="mt-2.5 pl-[18px]" data-testid="todo-note-form">
          <textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} autoFocus
            placeholder="Cine a sunat și ce s-a stabilit (ex.: semnează diseară, nu a primit emailul)"
            className="w-full max-w-xl resize-none rounded-lg border border-[#E5E7EB] px-2.5 py-1.5 text-sm text-[#373A45] focus:border-[#965EA7] focus:outline-none"
            data-testid="todo-note-input" />
          <div className="mt-1.5 flex gap-2">
            <button type="button" onClick={saveNote} disabled={busy === "note" || !note.trim()}
              className={`${ACTION} border-[#965EA7] bg-[#965EA7] text-white hover:bg-[#7A4986]`} data-testid="todo-note-save">
              {busy === "note" && <Loader2 size={12} className="animate-spin" />} Salvează
            </button>
            <button type="button" onClick={() => { setNoting(false); setNote(""); }}
              className={`${ACTION} border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]`}>
              Renunță
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
